// Draggable splitter for the tmux-style pane layout.
//
// Two variants, one component:
//   <Splitter direction="vertical"   onResize={pct => ...} />  // 6px wide,  col-resize, splits LEFT/RIGHT
//   <Splitter direction="horizontal" onResize={pct => ...} />  // 6px tall,  row-resize, splits TOP/BOTTOM
//
// onResize receives the new size of the FIRST pane as a percent of the
// splitter's parent container. The parent should be a CSS grid where the
// first track is `${size}%`, the splitter is `6px`, and the third is `1fr`.
//
// We constrain to [MIN_PERCENT, MAX_PERCENT] so panes never collapse to zero.

import React, { useRef } from 'react';

const MIN_PERCENT = 15;
const MAX_PERCENT = 85;

interface SplitterProps {
  direction: 'vertical' | 'horizontal';
  onResize: (percent: number) => void;
}

const Splitter: React.FC<SplitterProps> = ({ direction, onResize }) => {
  const draggingRef = useRef(false);

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    const splitterEl = e.currentTarget;
    const parent = splitterEl.parentElement;
    if (!parent) return;

    const rect = parent.getBoundingClientRect();
    draggingRef.current = true;

    // Visual feedback during drag.
    document.body.style.cursor = direction === 'vertical' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';

    function onMove(ev: PointerEvent) {
      if (!draggingRef.current) return;
      const next =
        direction === 'vertical'
          ? ((ev.clientX - rect.left) / rect.width) * 100
          : ((ev.clientY - rect.top) / rect.height) * 100;
      onResize(Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, next)));
    }

    function onUp() {
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    }

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }

  // Double-click resets to 50/50.
  function handleDoubleClick(e: React.MouseEvent<HTMLDivElement>) {
    e.preventDefault();
    onResize(50);
  }

  const isVertical = direction === 'vertical';

  return (
    <div
      role="separator"
      aria-orientation={isVertical ? 'vertical' : 'horizontal'}
      onPointerDown={handlePointerDown}
      onDoubleClick={handleDoubleClick}
      style={{
        cursor: isVertical ? 'col-resize' : 'row-resize',
        background: '#1f2937',
        userSelect: 'none',
        // Width/height come from the parent grid track; this fills it.
        width: '100%',
        height: '100%',
        transition: 'background-color 120ms ease-out',
      }}
      title={isVertical ? 'Drag to resize · double-click to reset' : 'Drag to resize · double-click to reset'}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(61,220,132,0.5)')}
      onMouseLeave={(e) => {
        if (!draggingRef.current) e.currentTarget.style.background = '#1f2937';
      }}
    />
  );
};

export default Splitter;
